import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { accountDetailPath } from '../lib/portal';
import { PageHeader } from '../components/ui/PageHeader';
import { useCrmData } from '../contexts/CrmDataContext';
import { usePortal } from '../contexts/PortalContext';
import { approveAccountTransfer, rejectAccountTransfer } from '../lib/accountTransfer';
import { formatTransferScope } from '../lib/accountTransferScope';
import { logPortalActivityQuiet } from '../lib/portalActivityLog';
import type { AccountTransferRequest } from '../types/crm';
import { clientById } from '../utils/crmHelpers';
import { formatSingaporeDateTime } from '../utils/dateTime';

export function ApprovalsPage() {
  const { kind: portal } = usePortal();
  const { data, canApproveTransfers, ownerLabel, refresh, loading } = useCrmData();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pending = useMemo(
    () =>
      [...data.transferRequests.filter((r) => r.status === 'pending')].sort((a, b) =>
        (b.created_at ?? '').localeCompare(a.created_at ?? ''),
      ),
    [data.transferRequests],
  );

  if (!canApproveTransfers) {
    return (
      <div className="approvals-page">
        <PageHeader
          title="Transfer approvals"
          description="Account transfer requests require Admin, Management, or IT access."
        />
        <div className="empty-state card">
          <p>You do not have permission to approve account transfers.</p>
        </div>
      </div>
    );
  }

  function logTransferDecision(
    request: AccountTransferRequest | undefined,
    action: 'approve' | 'reject',
    note?: string,
  ) {
    if (!request) return;
    const client = clientById(data.clients, request.client_id);
    logPortalActivityQuiet({
      portal,
      action,
      entityType: 'transfer',
      entityId: request.id,
      clientId: request.client_id,
      title: `Transfer ${action === 'approve' ? 'approved' : 'rejected'}: ${client?.company_name ?? 'Account'}`,
      detail: [
        `${ownerLabel(request.from_owner_id)} → ${ownerLabel(request.to_owner_id)}`,
        `Scope: ${formatTransferScope(request)}`,
        note?.trim() ? `Note: ${note.trim()}` : null,
      ]
        .filter(Boolean)
        .join('\n'),
    });
  }

  async function handleApprove(requestId: string) {
    const request = pending.find((r) => r.id === requestId);
    setBusyId(requestId);
    setError(null);
    try {
      await approveAccountTransfer(requestId);
      logTransferDecision(request, 'approve');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed.');
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(requestId: string) {
    const note = window.prompt('Rejection reason (optional):') ?? '';
    const request = pending.find((r) => r.id === requestId);
    setBusyId(requestId);
    setError(null);
    try {
      await rejectAccountTransfer(requestId, note);
      logTransferDecision(request, 'reject', note);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="approvals-page">
      <PageHeader
        title="Transfer approvals"
        description="Review and approve account salesperson-code transfers. Approval only changes the account code — projects and tasks keep their existing owners."
        actions={
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => void refresh()}
            disabled={loading || busyId != null}
          >
            Refresh
          </button>
        }
      />

      <div className="approvals-page__toolbar">
        <span className="page-count">
          {loading
            ? 'Loading…'
            : `${pending.length} pending request${pending.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading && pending.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading transfer requests…</p>
        </div>
      ) : pending.length === 0 ? (
        <div className="empty-state card approvals-page__empty">
          <p>No pending transfer requests.</p>
        </div>
      ) : (
        <ul className="transfer-approval-list">
          {pending.map((r) => {
            const client = clientById(data.clients, r.client_id);
            const applicant = data.users.find((u) => u.id === r.requested_by);
            return (
              <li key={r.id} className="transfer-approval-list__item card">
                <div className="transfer-approval-list__main">
                  <h2 className="transfer-approval-list__title">
                    {client ? (
                      <Link to={accountDetailPath(client.code)} className="transfer-approval-list__account">
                        {client.company_name}
                      </Link>
                    ) : (
                      'Unknown account'
                    )}
                    {client?.code ? (
                      <span className="transfer-approval-list__code">{client.code}</span>
                    ) : null}
                  </h2>

                  <dl className="transfer-approval-list__meta">
                    <div>
                      <dt>From</dt>
                      <dd className="mono">{ownerLabel(r.from_owner_id)}</dd>
                    </div>
                    <div>
                      <dt>To</dt>
                      <dd className="mono">{ownerLabel(r.to_owner_id)}</dd>
                    </div>
                    <div>
                      <dt>Scope</dt>
                      <dd>{formatTransferScope(r)}</dd>
                    </div>
                    <div>
                      <dt>Requested by</dt>
                      <dd>
                        {applicant?.display_name ?? applicant?.username ?? '—'}
                        {r.created_at ? (
                          <span className="transfer-approval-list__when">
                            {' '}
                            · {formatSingaporeDateTime(r.created_at)}
                          </span>
                        ) : null}
                      </dd>
                    </div>
                  </dl>

                  {r.message ? (
                    <p className="transfer-approval-list__message">{r.message}</p>
                  ) : null}
                </div>

                <div className="transfer-approval-list__actions">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={busyId === r.id}
                    onClick={() => void handleApprove(r.id)}
                  >
                    {busyId === r.id ? 'Working…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={busyId === r.id}
                    onClick={() => void handleReject(r.id)}
                  >
                    Reject
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
